import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { ASSETS_COLLECTION_INVENTORY_MOVE_QUERY } from "../../../constants/graphql/queries";
import ShipmentErrorModal from "./ShipmentErrorModal";
import ItemNotFoundModal from "./ItemNotFoundModal";
import ItemInventoryMove from "./ItemInventoryMove";
import LoadingDialog from "./LoadingDialog";
import SelectItemToMove from "./SelectItemToMove";
import useQueryParams from "src/hooks/useQueryParams";
import ConfirmMoveToSelectedLocationModal from "./ConfirmMoveToSelectedLocationModal";

export const ItemInventoryMoveContext = React.createContext();

// The "All" customer page routes with clientId "null" and projectId "all".
const selectedId = id => (id && id !== "null" && id !== "all" ? id : undefined);

export default function MoveBySearchModal({
  code,
  clientId,
  projectId,
  onSearchAllCustomers,
  matchMode,
  onClose,
  onSelect,
}) {
  const [exactMatchOnly, setExactMatchOnly] = useState(matchMode !== "broad");
  const scopeClientId = selectedId(clientId);
  const scopeProjectId = selectedId(projectId);
  const scoped = !!(scopeClientId || scopeProjectId);
  const params = useQueryParams();
  const movedAssetIds = params.get("movedAssetIds");
  const newRackLocation = params.get("newRackLocation");

  useEffect(() => {
    setExactMatchOnly(matchMode !== "broad");
  }, [code, matchMode]);

  const { loading, error, data } = useQuery(ASSETS_COLLECTION_INVENTORY_MOVE_QUERY, {
    variables: {
      search: code,
      clientId: scopeClientId,
      projectId: scopeProjectId,
      exactMatchOnly,
      hasImportedAssets: true,
      order: { item_id: "desc" },
      type: "Asset",
    },
  });
  const assets = data?.assetsCollection?.collection || [];
  const [asset] = assets;

  if (loading) {
    return <LoadingDialog onClose={onClose} />;
  }

  if (error) {
    return <ShipmentErrorModal onClose={onClose} errorMessage={error.message} />;
  }

  if (!asset) {
    // Switches the page to the "All" customer, so this and later scans
    // search every customer.
    const searchAllProps =
      scoped && onSearchAllCustomers
        ? {
            secondaryActionLabel: "Switch and Search All Customers",
            onSecondaryAction: onSearchAllCustomers,
          }
        : {};
    const scopeNote = scoped ? " This search only covered the selected customer." : "";

    if (exactMatchOnly) {
      return (
        <ItemNotFoundModal
          onClose={onClose}
          title="No Exact Matches Found"
          message={`No exact matches were found for that search.${scopeNote} You can include similar matches to broaden the results.`}
          actionLabel="Include Similar Matches"
          onAction={() => setExactMatchOnly(false)}
          {...searchAllProps}
        />
      );
    }

    return (
      <ItemNotFoundModal
        onClose={onClose}
        title="No Matches Found"
        message={`We couldn't find anything for that search.${scopeNote} Try a different search term.`}
        {...searchAllProps}
      />
    );
  }

  if (assets.length > 1) {
    return <SelectItemToMove assets={assets} onSelect={onSelect} onClose={onClose} />;
  }

  if (movedAssetIds && newRackLocation && asset?.rackLocations?.length === 1) {
    return <ConfirmMoveToSelectedLocationModal asset={asset} />;
  }

  return <ItemInventoryMove data={data} onClose={onClose} />;
}
